import React, { useState } from "react";
import { Box, Paper, Typography, IconButton, Button } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { styled } from "@mui/system";
import { videoList } from "./data";
import playStoreImage from "../images/PlayStoreImage.png";
import appleLogo from "../images/apple-logo (1).png";

const ThumbnailContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  gap: theme.spacing(1),
  width: { xs: "100%", sm: "100%", md: "100%", lg: "100%", xl: "20%" },
  overflowX: "auto",
  padding: theme.spacing(1),

  // For larger screens, switch to vertical column layout
  [theme.breakpoints.up("lg")]: {
    flexDirection: "column",
    gap: theme.spacing(2),
    overflowX: "hidden", // Hide horizontal scrollbar on desktop
    overflowY: "auto",
  },
}));

const VideoSection = () => {
  const [currentVideo, setCurrentVideo] = useState(videoList[0]);

  const renderVideoPlayer = () => {
    if (currentVideo.type === "youtube") {
      return (
        <iframe
          width="100%"
          height="100%"
          src={currentVideo.url}
          title={currentVideo.name}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={{
            borderRadius: 4,
            padding: 4,
          }}
        ></iframe>
      );
    } else {
      return (
        <video
          controls
          autoPlay
          muted
          key={currentVideo.url}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            borderRadius: 4,
            padding: 4,
          }}
        >
          <source src={currentVideo.url} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      );
    }
  };

  return (
    <>
      <Typography
        sx={{
          color: "#002367",
          fontFamily: "Light",
          fontSize: { xs: 24, sm: 32, md: 42 }, // Responsive font size
          fontWeight: "700",
          lineHeight: "35px",
          textAlign: "center",
          marginTop: 12,
          marginBottom: 5,
          justifySelf: "center",
        }}
      >
        Introduction to LLM Insight
      </Typography>
      {/* Video section */}
      <Box
        sx={{
          width: { xs: "100%", sm: "90%", md: "80%", lg: "80%", xl: "80%" },
          justifySelf: "center",
          display: "flex",
          flexDirection: "column",
          height: { xs: "auto", sm: "auto", md: "60vh" },
          backgroundColor: "white",
          boxShadow: "0 0 10px rgba(0, 0, 0, 0.2)",
          border: "1px solid rgba(0, 0, 0, 0.3)",
          borderRadius: 2,
          margin: "0 auto",
          padding: 1,
          marginBottom: 6,
        }}
      >
        {/* Thumbnail List on Top for Smaller Screens */}
        <Box
          sx={{
            display: {
              xs: "flex",
              sm: "flex",
              md: "flex",
              lg: "none",
              xl: "none",
            },
            width: { xs: "90%", sm: "90%", md: "90%", lg: "90%", xl: "20%" },
            padding: 2,
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h6"
            gutterBottom
            sx={{ color: "#073D8D", textAlign: "center", width: "100%" }}
          >
            Watch More
          </Typography>
          <Box
            sx={{
              width: "100%",
              maxHeight: "200px", // Set a fixed height for mobile view
              overflowY: "auto", // Enable vertical scrolling
            }}
          >
            <ThumbnailContainer
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 1,
                width: "100%",
                overflowX: "auto",
                flexWrap: "nowrap", // Prevent wrapping
              }}
            >
              {videoList.map((video, index) => (
                <Box
                  key={index}
                  sx={{
                    cursor: "pointer",
                    position: "relative",
                    borderRadius: 1,
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    padding: 1,
                    minWidth: "80px", // Ensure minimum width
                    maxWidth: "100px", // Limit maximum width
                    flex: "0 0 auto", // Prevent growth/shrink
                    backgroundColor:
                      currentVideo.id === video.id ? "#d9d9d9" : "transparent",
                    transition:
                      "transform 0.3s ease, background-color 0.3s ease",
                    "&:hover": {
                      "& img": {
                        transform: "scale(1.05)",
                      },
                    },
                  }}
                  onClick={() => setCurrentVideo(video)}
                >
                  {currentVideo.id === video.id && (
                    <PlayArrowIcon sx={{ color: "#073D8D" }} />
                  )}
                  <img
                    src={video.thumbnail}
                    alt={video.name}
                    style={{
                      width: "100%",
                      height: "50px",
                      objectFit: "cover",
                      transition: "transform 0.3s ease",
                    }}
                  />
                </Box>
              ))}
            </ThumbnailContainer>
          </Box>
        </Box>

        <Paper
          elevation={0}
          sx={{
            flex: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: {
              xs: "column",
              sm: "column",
              md: "column",
              lg: "row",
              xl: "row",
            },
            borderRadius: 1,
            overflow: "hidden",
            backgroundColor: "white",
            borderRadius: 2,
          }}
        >
          {/* Main Video Player */}
          <Box
            sx={{
              width: { xs: "90%", sm: "90%", md: "90%", lg: "80%", xl: "80%" },
              padding: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto",
              height: "100%",
            }}
          >
            {renderVideoPlayer()}
          </Box>

          {/* Thumbnail List for Larger Screens */}
          <Box
            sx={{
              display: {
                xs: "none",
                sm: "none",
                md: "none",
                lg: "flex",
                xl: "flex",
              },
              width: { lg: "20%", xl: "20%" },
              // padding: 2,
              flexDirection: "column",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: "#073D8D", textAlign: "center" }}
            >
              Watch More
            </Typography>
            <Box
              sx={{
                width: "100%",
                height: "calc(100% - 40px)", // Adjust for typography height
                overflowY: "auto", // Enable vertical scrolling
              }}
            >
              <ThumbnailContainer
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  width: "90%",
                  height: "auto", // Allow container to expand
                  flexWrap: "nowrap", // Prevent wrapping
                }}
              >
                {videoList.map((video, index) => (
                  <Box
                    key={index}
                    sx={{
                      cursor: "pointer",
                      width: "90%",
                      position: "relative",
                      borderRadius: 2,
                      overflow: "hidden",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      padding: 1,
                      minHeight: "60px", // Ensure consistent height
                      flex: "0 0 auto", // Prevent growth/shrink
                      backgroundColor:
                        currentVideo.id === video.id
                          ? "#d9d9d9"
                          : "transparent",
                      transition:
                        "transform 0.3s ease, background-color 0.3s ease",
                      "&:hover": {
                        "& img": {
                          transform: "scale(1.05)",
                        },
                      },
                    }}
                    onClick={() => setCurrentVideo(video)}
                  >
                    {currentVideo.id === video.id && (
                      <PlayArrowIcon sx={{ color: "#073D8D" }} />
                    )}
                    <img
                      src={video.thumbnail}
                      alt={video.name}
                      style={{
                        width: "100%",
                        height: "auto",
                        maxHeight: "80px",
                        // objectFit: "contain",
                        transition: "transform 0.3s ease",
                        borderRadius: 4,
                      }}
                    />
                  </Box>
                ))}
              </ThumbnailContainer>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* Download App Section */}
      <Box
        sx={{
          width: { xs: "100%", sm: "90%", md: "80%", lg: "80%", xl: "80%" },
          margin: "0 auto",
          marginBottom: 6,
        }}
      >
        <Typography
          sx={{
            color: "#002367",
            fontFamily: "Light",
            fontSize: { xs: 24, sm: 32, md: 42 },
            fontWeight: "700",
            lineHeight: "35px",
            textAlign: "center",
            marginBottom: 4,
          }}
        >
          Download the App
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "center",
            alignItems: "center",
            gap: 3,
            padding: 3,
            backgroundColor: "white",
            boxShadow: "0 0 10px rgba(0, 0, 0, 0.2)",
            border: "1px solid rgba(0, 0, 0, 0.3)",
            borderRadius: 2,
          }}
        >
          {/* Google Play Store Button */}
          <Button
            variant="outlined"
            onClick={() => window.open('https://play.google.com/store/apps/details?id=ai.llminsightsaas&hl=en', '_blank')}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              padding: "12px 24px",
              border: "2px solid #073D8D",
              borderRadius: 2,
              color: "#073D8D",
              backgroundColor: "white",
              minWidth: "200px",
              "&:hover": {
                backgroundColor: "#073D8D",
                color: "white",
                border: "2px solid #073D8D",
              },
            }}
          >
            <Box
              component="img"
              src={playStoreImage}
              alt="Google Play"
              sx={{
                width: 32,
                height: 32,
              }}
            />
            <Box sx={{ textAlign: "left" }}>
              <Typography
                variant="caption"
                sx={{ fontSize: "10px", lineHeight: 1 }}
              >
                Download on the
              </Typography>
              <Typography
                variant="body1"
                sx={{ fontWeight: "bold", lineHeight: 1 }}
              >
                Play Store
              </Typography>
            </Box>
          </Button>

          {/* Apple App Store Button */}
          <Button
            variant="outlined"
            onClick={() => window.open('https://apps.apple.com/us/app/llm-insight-saas/id6747241172', '_blank')}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              padding: "12px 24px",
              border: "2px solid #073D8D",
              borderRadius: 2,
              color: "#073D8D",
              backgroundColor: "white",
              minWidth: "200px",
              "&:hover": {
                backgroundColor: "#073D8D",
                color: "white",
                border: "2px solid #073D8D",
              },
            }}
          >
            <Box
              component="img"
              src={appleLogo}
              alt="Apple App Store"
              sx={{
                width: 32,
                height: 32,
              }}
            />
            <Box sx={{ textAlign: "left" }}>
              <Typography
                variant="caption"
                sx={{ fontSize: "10px", lineHeight: 1 }}
              >
                Download on the
              </Typography>
              <Typography
                variant="body1"
                sx={{ fontWeight: "bold", lineHeight: 1 }}
              >
                App Store
              </Typography>
            </Box>
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default VideoSection;
